import { OrderService } from './services/order';
import { ToastMessage } from './toast';
import { DateUtility } from './date';

export const MAX_RESUME_ORDER_IDS = 10;

function getResumeErrorMessage(err) {
  if (typeof err?.error === 'string') return err.error;
  if (typeof err?.error?.message === 'string') return err.error.message;
  if (typeof err?.message === 'string') return err.message;
  return null;
}

export function buildResumeSuccessMessage(response) {
  const summary = response?.summary || {};
  const resumed = summary.resumed ?? 0;
  const refreshStarted = summary.refreshStarted ?? 0;
  const blocked = summary.blocked ?? 0;

  if (response?.started || refreshStarted > 0) {
    const parts = [];
    if (refreshStarted > 0) {
      parts.push(
        refreshStarted === 1
          ? '1 order queued for background reassignment'
          : `${refreshStarted} orders queued for background reassignment`,
      );
    }
    if (resumed > 0) {
      parts.push(
        resumed === 1
          ? '1 order resumed immediately'
          : `${resumed} orders resumed immediately`,
      );
    }
    if (blocked > 0) {
      parts.push(
        blocked === 1
          ? '1 order could not be processed'
          : `${blocked} orders could not be processed`,
      );
    }
    return parts.join('. ') || 'Resume fulfillment started.';
  }

  if (resumed > 0 && blocked === 0) {
    return resumed === 1
      ? 'Order fulfillment resumed.'
      : `${resumed} orders resumed successfully.`;
  }

  if (resumed > 0 || blocked > 0) {
    const parts = [];
    if (resumed > 0) parts.push(`${resumed} resumed`);
    if (blocked > 0) parts.push(`${blocked} could not be processed`);
    return `Resume fulfillment completed (${parts.join(', ')}).`;
  }

  return 'Resume fulfillment completed.';
}

export async function resumePausedOrders({ warehouseId, orderIds }) {
  if (!warehouseId) {
    ToastMessage.error('Select a warehouse before recalculating fulfillment.');
    return { ok: false };
  }

  const uniqueOrderIds = [...new Set((orderIds || []).map(String))].filter(Boolean);

  if (!uniqueOrderIds.length) {
    ToastMessage.error('Select at least one paused order.');
    return { ok: false };
  }

  if (uniqueOrderIds.length > MAX_RESUME_ORDER_IDS) {
    ToastMessage.error(`You can resume up to ${MAX_RESUME_ORDER_IDS} orders at a time.`);
    return { ok: false };
  }

  try {
    const response = await OrderService.resumeFulfillment({
      warehouseId,
      orderIds: uniqueOrderIds,
    });

    const message = buildResumeSuccessMessage(response);
    if (response?.started || (response?.summary?.refreshStarted ?? 0) > 0) {
      ToastMessage.success(message);
    } else if (
      (response?.summary?.blocked ?? 0) > 0 &&
      (response?.summary?.resumed ?? 0) === 0
    ) {
      ToastMessage.warning(message);
    } else {
      ToastMessage.success(message);
    }

    return { ok: true, response };
  } catch (error) {
    if (error.details?.retryAfterSeconds) {
      const cooldownPeriod = DateUtility.formatDurationFromSeconds(
        error.details.retryAfterSeconds,
      );
      ToastMessage.error(
        `Please try again in ${cooldownPeriod}; fulfillment was recently resumed for one of the selected orders.`,
      );
      return { ok: false, error };
    }

    const message = getResumeErrorMessage(error);
    if (message) {
      ToastMessage.error(message);
    } else {
      ToastMessage.apiError(error);
    }
    return { ok: false, error };
  }
}
